import React, { FC, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { AppDispatch } from 'src/redux/store';
import { useSurvey } from 'src/contexts/SurveyContext';
import { buildCertificationHubPath } from 'src/routes/renterPaths';
import {
  fetchFileQuestions,
  selectFileQuestions,
} from 'src/redux/slices/Survey/surveyFileQuestionsSlice';
import type {
  DocCard,
  DocQuestion,
  DocFile,
} from 'src/api/dataVault/surveyFileQuestions';

interface FileSlotCardProps {
  file: DocFile | null;
}

const FileSlotCard: FC<FileSlotCardProps> = ({ file }) => {
  if (file) {
    const rejected = file.is_rejected === true;
    return (
      <Box
        role="img"
        aria-label={rejected ? 'Rejected — please re-upload' : 'Approved'}
        sx={{
          width: 40,
          height: 52,
          bgcolor: rejected ? 'warning.lighter' : 'success.lighter',
          border: '1px solid',
          borderColor: rejected ? 'warning.main' : 'success.light',
          borderRadius: '4px 0 4px 4px',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <DescriptionIcon
          sx={{
            position: 'absolute',
            top: 8,
            left: 10,
            fontSize: 20,
            color: rejected ? 'warning.dark' : 'success.dark',
          }}
        />
        {rejected ? (
          <ReportProblemOutlinedIcon
            sx={{
              position: 'absolute',
              top: 32,
              left: 13,
              fontSize: 14,
              color: 'warning.dark',
            }}
          />
        ) : (
          <CheckCircleIcon
            sx={{
              position: 'absolute',
              top: 32,
              left: 13,
              fontSize: 14,
              color: 'success.dark',
            }}
          />
        )}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 40,
        height: 52,
        bgcolor: 'action.hover',
        border: '1.5px solid',
        borderColor: 'grey.400',
        borderRadius: '4px 0 4px 4px',
        position: 'relative',
        flexShrink: 0,
      }}
    >
      <UploadFileIcon
        sx={{
          position: 'absolute',
          top: 10,
          left: 11,
          fontSize: 18,
          color: 'text.secondary',
        }}
      />
      <Typography
        sx={{
          position: 'absolute',
          top: 32,
          left: 12,
          fontSize: 9,
          fontWeight: 700,
          color: 'text.secondary',
          fontFamily: 'Public Sans',
        }}
      >
        add
      </Typography>
    </Box>
  );
};

interface QuestionRowProps {
  question: DocQuestion;
  onQuestionTap: (qitId: number) => void;
}

const QuestionRow: FC<QuestionRowProps> = ({ question, onQuestionTap }) => {
  // Rejected files surface first so the renter sees what needs re-uploading.
  const orderedFiles = useMemo(
    () =>
      [...question.files].sort(
        (a, b) =>
          Number(b.is_rejected ?? false) - Number(a.is_rejected ?? false)
      ),
    [question.files]
  );
  const slots = Array.from(
    { length: question.files_required },
    (_, i) => orderedFiles[i] ?? null
  );

  const handleTap = useCallback(() => {
    onQuestionTap(question.qit_id);
  }, [onQuestionTap, question.qit_id]);

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleTap();
      }
    },
    [handleTap]
  );

  return (
    <Box
      role="button"
      aria-label={`Manage ${question.question_label}`}
      tabIndex={0}
      onClick={handleTap}
      onKeyDown={handleRowKeyDown}
      sx={{
        px: '14px',
        py: '12px',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 'normal',
          fontFamily: 'Public Sans',
          color: 'text.primary',
        }}
      >
        {question.question_label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
          alignItems: 'center',
        }}
      >
        {slots.map((file, i) => (
          <FileSlotCard key={i} file={file} />
        ))}
        <Box sx={{ flex: 1 }} />
        <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      </Box>
    </Box>
  );
};

const RowDivider: FC = () => (
  <Box sx={{ height: '1px', width: '100%', bgcolor: 'divider' }} />
);

interface DocCardViewProps {
  card: DocCard;
  onQuestionTap: (qitId: number) => void;
}

const DocCardView: FC<DocCardViewProps> = ({ card, onQuestionTap }) => {
  const theme = useTheme();
  // Questions with rejected files rise to the top of the card.
  const orderedQuestions = useMemo(
    () =>
      [...card.questions].sort((a, b) => {
        const aRejected = a.files.some((f) => f.is_rejected) ? 0 : 1;
        const bRejected = b.files.some((f) => f.is_rejected) ? 0 : 1;
        return aRejected - bRejected;
      }),
    [card.questions]
  );
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: `0 2px 6px ${alpha(theme.palette.text.secondary, 0.08)}`,
        flexShrink: 0,
      }}
    >
      <Box sx={{ bgcolor: 'action.selected', px: '14px', py: '10px' }}>
        {card.is_multi ? (
          <>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: 'text.secondary',
                fontFamily: 'Public Sans',
              }}
            >
              {card.group_name}
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: 'text.primary',
                fontFamily: 'Public Sans',
              }}
            >
              {card.instance_label}
            </Typography>
          </>
        ) : (
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: 'Public Sans',
            }}
          >
            {card.group_name}
          </Typography>
        )}
      </Box>
      {orderedQuestions.map((q, idx) => (
        <React.Fragment key={q.qit_id}>
          {idx > 0 && <RowDivider />}
          <QuestionRow question={q} onQuestionTap={onQuestionTap} />
        </React.Fragment>
      ))}
    </Box>
  );
};

const MyDocumentsPage: FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    surveyId,
    targetUserId,
    actingOnMemberId,
    isOnBehalf,
    memberName,
    certId,
    certName,
    propertyName,
    effectiveDate,
  } = useSurvey();

  // Back returns to the Certification Hub (the entry point) when the cert is
  // known; falls back to the Survey Explorer for profile surveys / early render.
  const backRoute =
    certId != null && targetUserId
      ? buildCertificationHubPath(certId, targetUserId)
      : actingOnMemberId
      ? `/renter/survey/${surveyId}/members/${actingOnMemberId}`
      : `/renter/survey/${surveyId}`;

  const entry = useSelector(selectFileQuestions(surveyId, actingOnMemberId));

  useEffect(() => {
    if (!surveyId) {
      return;
    }
    // Always force-refresh on mount: the user may have just returned from the
    // interview after uploading or deleting a file. Without forceRefresh the
    // 30-second stale window would serve the old slot state.
    dispatch(
      fetchFileQuestions({ surveyId, actingOnMemberId, forceRefresh: true })
    );
  }, [dispatch, surveyId, actingOnMemberId]);

  const handleQuestionTap = useCallback(
    (qitId: number) => {
      // TODO (ENG-2969 follow-up): pass answer_group_id so the interview can
      // target the exact multi-group instance the renter tapped. This requires
      // the backend IPE to accept an answer_group_id URL param and
      // serialize_next_group to respect it instead of always picking the latest
      // instance. Until that IPE work lands, the interview opens the most-recent
      // instance for shared multi-group QITs.
      const base = actingOnMemberId
        ? `/renter/survey/${surveyId}/members/${actingOnMemberId}/my-documents/answer`
        : `/renter/survey/${surveyId}/my-documents/answer`;
      navigate(`${base}?qits=${qitId}`);
    },
    [navigate, surveyId, actingOnMemberId]
  );

  const handleBack = useCallback(() => {
    navigate(backRoute);
  }, [navigate, backRoute]);

  const handleRetry = useCallback(() => {
    dispatch(
      fetchFileQuestions({ surveyId, actingOnMemberId, forceRefresh: true })
    );
  }, [dispatch, surveyId, actingOnMemberId]);

  const isLoading = !entry || entry.status === 'loading';
  const isFailed = entry?.status === 'failed';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.neutral',
      }}
    >
      {/* ── Page chrome — always visible so navigation is never blocked ── */}
      <Box
        sx={{
          bgcolor: 'primary.dark',
          px: '20px',
          pt: '20px',
          pb: '20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.75,
        }}
      >
        <IconButton
          onClick={handleBack}
          aria-label="Go back"
          size="small"
          sx={{
            backgroundColor: 'common.white',
            color: 'grey.900',
            width: 34,
            height: 34,
            borderRadius: '50%',
            boxShadow: `0px 1px 4px ${alpha(
              theme.palette.text.secondary,
              0.06
            )}`,
            flexShrink: 0,
            '&:hover': { backgroundColor: 'grey.100' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 700,
              color: 'common.white',
              fontFamily: 'Public Sans',
            }}
          >
            {isOnBehalf && memberName
              ? `${memberName}'s Documents`
              : 'My Documents'}
          </Typography>
          {certName && (
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                color: 'common.white',
                opacity: 0.93,
                fontFamily: 'Public Sans',
                mt: '6px',
              }}
            >
              {certName}
            </Typography>
          )}
          {propertyName && (
            <Typography
              sx={{
                fontSize: 13,
                color: 'common.white',
                opacity: 0.6,
                fontFamily: 'Public Sans',
              }}
            >
              {propertyName}
              {effectiveDate ? `  ·  ${effectiveDate}` : ''}
            </Typography>
          )}
        </Box>
      </Box>

      {isOnBehalf && (
        <Box
          sx={{
            bgcolor: 'warning.lighter',
            borderBottom: '1px solid',
            borderBottomColor: 'warning.light',
            px: '16px',
            py: '10px',
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: 'warning.dark',
              fontFamily: 'Public Sans',
            }}
          >
            {`You are reviewing files on behalf of ${
              memberName ?? 'this member'
            }.`}
          </Typography>
        </Box>
      )}

      {/* ── Content area — branches on load / failed / empty / cards ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: '16px',
          py: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress aria-label="Loading documents" />
          </Box>
        ) : isFailed ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <Typography
              role="alert"
              sx={{ color: 'text.secondary', fontSize: 14 }}
            >
              Failed to load documents.{' '}
              <Button
                size="small"
                variant="outlined"
                onClick={handleRetry}
                sx={{ textTransform: 'none', ml: 1 }}
              >
                Retry
              </Button>
            </Typography>
          </Box>
        ) : entry.cards.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              textAlign: 'center',
              gap: 2,
              py: 6,
            }}
          >
            <FolderOpenIcon sx={{ fontSize: 48, color: 'primary.light' }} />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'Public Sans', fontWeight: 700 }}
            >
              No documents yet
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: 'text.secondary',
                fontFamily: 'Public Sans',
                maxWidth: 280,
              }}
            >
              Start answering your questionnaire to see your document uploads
              here.
            </Typography>
          </Box>
        ) : (
          entry.cards.map((card) => (
            <DocCardView
              key={
                card.is_multi
                  ? `${card.group_id}-${card.answer_group_id}`
                  : String(card.group_id)
              }
              card={card}
              onQuestionTap={handleQuestionTap}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default MyDocumentsPage;
